import { Mode, applyMode } from '@amzn/awsui-global-styles';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { usePersistedState } from '../hooks/useLocalStorage';
import { parseEmbedParams } from '../utils/embedUtils';

interface ThemeContextType {
  mode: Mode;
  toggleTheme: () => void;
  setMode: (mode: Mode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // useState with lazy initializer guarantees the value is computed exactly once per mount.
  // Uses the shared parseEmbedParams helper — single source of truth for embed detection.
  const [embedded] = useState(() => parseEmbedParams().isEmbedded);

  // In embedded mode, skip localStorage — parent will send the real theme via postMessage.
  // In standalone mode, persist to localStorage as before.
  const [persistedMode, setPersistedMode] = usePersistedState<Mode>('themeMode', Mode.Dark);
  const [embedMode, setEmbedMode] = useState<Mode>(Mode.Dark);

  const mode = embedded ? embedMode : persistedMode;
  const setMode = embedded ? setEmbedMode : setPersistedMode;

  useEffect(() => {
    applyMode(mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === Mode.Light ? Mode.Dark : Mode.Light));
  };

  return <ThemeContext.Provider value={{ mode, toggleTheme, setMode }}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
